import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TButtonInstance } from 'widgets/global/Button';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TShowMoreInstance } from 'core/global/ShowMore';
import { buildFormSelector } from '../../utils';

type TResponse = TServerResponse & {
    messageTitle?: string;
    messageBody?: string;
};

/**
 * @param EmailSubscribe Base widget for extending
 * @returns MCEmailSubscribe widget
 */
function EmailSubscribeGeneralClassCreator(EmailSubscribe: import('widgets/forms/ajax/EmailSubscribe').TEmailSubscribe) {
    /**
     * @category widgets
     * @subcategory forms/ajax
     * @class MCEmailSubscribe
     * @augments EmailSubscribe
     * @classdesc Serves Marketing Cloud/Klaviyo email subscription form in footer.
     * The main purpose EmailSubscribeGeneral widget is to allow send/validate the request to server and handles server response
     * @property {string} data-widget - Widget name `emailSubscribe`
     * @property {string} data-event-submit - Event listener for form submission
     *
     * @example <caption>Example of EmailSubscribeGeneral widget usage</caption>
     * <form
     *     class="b-newsletters"
     *     aria-label="${Resource.msg('subscribe.label', 'global', null)}"
     *     name="subscribe-form"
     *     action="${URLUtils.url('EmailSubscribe-Subscribe')}"
     *     method="POST"
     *     data-signup-email="${emailsubscribeForm.email.htmlName}"
     *     data-accessibility-announcement-msg="${Resource.msg('subscribe.alert', 'global', null)}"
     *     data-is-collapsed-form="false"
     *     data-widget="emailSubscribe"
     *     data-event-submit.prevent="handleSubmit"
     * >
     *     <div class="b-newsletters-caption">
     *         <iscontentasset aid="footer-newsletters" local="${true}" />
     *     </div>
     *     <div
     *         class="b-newsletters-group"
     *         data-ref="additionalFields"
     *         data-tau="newsletters_group"
     *     >
     *         ... form fields
     *     </div>
     *     <div
     *         class="b-newsletters-message_success"
     *         data-ref="successMessage"
     *         hidden="hidden"
     *         data-tau="newsletters_message_success"
     *     ></div>
     * </form>
     */
    class EmailSubscribeGeneral extends EmailSubscribe {
        prefs() {
            return {
                isCollapsedForm: false,
                hideFormOnSubmit: false,
                openFormClass: 'm-open',
                messageClasses: {
                    success: 'm-success',
                    info: 'm-info',
                    error: 'm-error'
                },
                gtmSubscriptionSource: '',
                friendlyCaptchaEnabled: false,
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init(): void {
            super.init();

            if (this.prefs().friendlyCaptchaEnabled) {
                const form = this.ref('self').get() as HTMLFormElement;
                const selector = buildFormSelector(form, true);

                if (selector) {
                    this.eventBus().emit('captcha.init', selector);
                }
            }
        }

        /**
         * @description Handles email field focus
         */
        onInputEmailFocus() {
            if (this.prefs().isCollapsedForm) {
                this.showRestoreFormButton();
                this.showAdditionalFields();
            }
        }

        /**
         * @description Shows additional fields if input is focused and a non navigation key is pressed
         * @param _ Source of keydown event
         * @param event Event object
         */
        onInputEmailKeydown(_: HTMLElement, event: KeyboardEvent) {
            const navigationKeys = ['ArrowDown', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'Shift', 'Tab'];

            if (!navigationKeys.includes(event.key)) {
                if (this.prefs().isCollapsedForm) {
                    this.showRestoreFormButton();
                    this.showAdditionalFields();
                }
            }
        }

        /**
         * @description Show additional form fields
         */
        showAdditionalFields() {
            this.ref('self').addClass(this.prefs().openFormClass);
            this.ref('additionalFields').show();
        }

        /**
         * @description Hide additional form fields
         */
        hideAdditionalFields() {
            this.ref('self').removeClass(this.prefs().openFormClass);
            this.ref('additionalFields').hide();
        }

        /**
         * @description Show form content
         */
        showFormContent() {
            this.ref('formContent').show();
        }

        /**
         * @description Hide form content
         */
        hideFormContent() {
            this.ref('formContent').hide();
        }

        /**
         * @description Show restore form button
         */
        showRestoreFormButton() {
            this.getById('restoreFormButton', (restoreFormButton: TButtonInstance) => restoreFormButton.show());
        }

        /**
         * @description Hide restore form button
         */
        hideRestoreFormButton() {
            this.getById('restoreFormButton', (restoreFormButton: TButtonInstance) => restoreFormButton.hide());
        }

        /**
         * @description Set message text
         * @param refElement Ref element instance
         * @param text Message text
         */
        setMessageText(refElement: TRefElementInstance, text: string) {
            if (!refElement) {
                return;
            }

            if (text) {
                refElement.setText(text).show();
            } else {
                refElement.setText('').hide();
            }
        }

        /**
         * @description Set message
         * @param data Server JSON response once form submitted
         */
        setMessage(data: TResponse) {
            const messageContainerRef = this.ref('messageContainer');
            const messageTitleRef = this.ref('messageTitle');
            const messageBodyRef = this.ref('messageBody');

            if (data.success) {
                messageContainerRef.addClass(this.prefs().messageClasses.success);
            } else {
                messageContainerRef.addClass(this.prefs().messageClasses.error);
            }

            if (data.messageType && typeof data.messageType === 'string') {
                messageContainerRef.addClass(this.prefs().messageClasses[data.messageType] || '');
            }

            this.setMessageText(messageTitleRef, data.messageTitle ?? '');

            if (data.additionalUrl && data.messageBody) {
                const bodyWithLink = `<a href="${data.additionalUrl}">${data.messageBody}</a>`;

                messageBodyRef.setHtml(bodyWithLink).show();
            } else {
                this.setMessageText(messageBodyRef, data.messageBody ?? '');
            }
        }

        /**
         * @description Handles server response
         * @emits "alert.show"
         * @param data Server JSON response once form submitted
         */
        onSubmitted(data: TResponse) {
            if (data.success && data.isReload) {
                window.location.reload();

                return;
            }

            if (data.fieldErrors) {
                Object.entries(data.fieldErrors).forEach(([name, errorMsg]) => {
                    this.getById(name, (formField: TBasicInputInstance) => formField.setError(errorMsg));
                });
            } else {
                this.setMessage(data);

                if (this.prefs().hideFormOnSubmit) {
                    this.hideRestoreFormButton();
                    this.hideAdditionalFields();
                    this.hideFormContent();
                }

                /**
                 * @description Global event to show alert
                 * @event "alert.show"
                 */
                if (data.success) {
                    this.eventBus().emit('newsletter.subscribed', this, data);
                    this.eventBus().emit('alert.show', {
                        accessibilityAlert: this.prefs().accessibilityAnnouncementMsg
                    });

                    const messageContainerRef = this.ref('messageContainer').get();

                    if (messageContainerRef) {
                        messageContainerRef?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                } else {
                    this.eventBus().emit('newsletter.not.subscribed', this);
                }
            }

            if (this.prefs().friendlyCaptchaEnabled) {
                const form = this.ref('self').get() as HTMLFormElement;

                if (!data?.success) {
                    const selector = buildFormSelector(form, true);

                    this.eventBus().emit('captcha.reset.on.error', selector);
                }

                const captchaFormInput = form?.querySelector<HTMLInputElement>(`.m-captcha-solution input`) as HTMLInputElement;

                captchaFormInput.value = '';
            }
        }

        /**
         * @description Reset form fields
         */
        resetFormFields() {
            this.eachField((formField: TBasicInputInstance) => {
                formField.setValue('');
                formField.clearError();
            });

            this.getByIdDeep('showMore', (showMore: TShowMoreInstance) => showMore.hideContent());
        }

        /**
         * @description Handles restore form button click
         * @param restoreButton Restore form button instance
         */
        handleRestoreFormButtonClick(restoreButton: TButtonInstance) {
            this.hideAdditionalFields();
            this.resetFormFields();

            restoreButton.hide();
        }
    }

    return EmailSubscribeGeneral;
}

export type TEmailSubscribeGeneral = ReturnType<typeof EmailSubscribeGeneralClassCreator>;

export type TEmailSubscribeGeneralInstance = InstanceType<TEmailSubscribeGeneral>;

export default EmailSubscribeGeneralClassCreator;
